import React from "react";
import classnames from "classnames";
import Select from "react-select";
import { Link } from "react-router";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Row,
  Label,
  FormGroup,
  InputGroup,
  Input,
} from "reactstrap";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import ImageUploadContainer from "../../User/adding-user/ImageUploadContainer";
import type { EditSite } from "@/services/siteServices/siteActions";

/** The site record EditSite saves (`LeanSite`, shared/apiTypes). */
export type SiteRecord = Parameters<typeof EditSite>[0];

type SelectOption = { value: string; label: string };
type DesignOption = { value: { _id: string }; label: string };

type UpdateSiteViewProps = {
  site: SiteRecord | null;
  countryOptions: readonly SelectOption[];
  languageOptions: readonly SelectOption[];
  designOptions: DesignOption[];
  loading: boolean;
  canEdit: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeCountry: (option: SelectOption | null) => void;
  onChangeLanguage: (option: SelectOption | null) => void;
  onChangeDesign: (option: DesignOption | null) => void;
  onToggleActive: () => void;
  onImageUpload: (img: { data?: string; imgName?: string; secure_url?: string; context?: unknown } | null) => void;
  onClickUpdate: () => void;
  backLink: string;
};

export default function UpdateSiteView({
  site,
  countryOptions,
  languageOptions,
  designOptions,
  loading,
  canEdit,
  onChange,
  onChangeCountry,
  onChangeLanguage,
  onChangeDesign,
  onToggleActive,
  onImageUpload,
  onClickUpdate,
  backLink,
}: UpdateSiteViewProps) {
  if (!site) return null;

  return (
    <>
      <SimpleHeader name="Update Site" parentName="Sites" />
      <Container className="mt--6" fluid>
        <Row>
          <Col className="order-xl-1" xl="8">
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">Update Site</h3>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                {/* Name */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Name</Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Name"
                        name="name"
                        type="text"
                        value={site.name || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Domain */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Domain</Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Domain"
                        name="domain"
                        type="textarea"
                        value={site.domain || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Canonical Url */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Canonical Url</Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Canonical Url"
                        name="canonial_url"
                        type="text"
                        value={site.canonial_url || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Country */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Country Code</Label>
                  <Col md="7">
                    <Select
                      value={countryOptions.find(o => o.value === site.country_code) || null}
                      options={countryOptions}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      onChange={onChangeCountry}
                    />
                  </Col>
                </FormGroup>

                {/* Language */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Language</Label>
                  <Col md="7">
                    <Select
                      value={languageOptions.find(o => o.value === site.language_code) || null}
                      options={languageOptions}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      onChange={onChangeLanguage}
                    />
                  </Col>
                </FormGroup>

                {/* Design */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Design</Label>
                  <Col md="7">
                    <Select
                      value={
                        site.design?._id
                          ? designOptions.find(o => o.value._id === site.design?._id) || null
                          : null
                      }
                      options={designOptions}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      onChange={onChangeDesign}
                    />
                  </Col>
                </FormGroup>

                {/* Active */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Active</Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <label className="custom-toggle custom-toggle-danger mr-1">
                        <input
                          checked={!!site.active}
                          onChange={onToggleActive}
                          name="active"
                          type="checkbox"
                        />
                        <span
                          className="custom-toggle-slider rounded-circle"
                          data-label-off="No"
                          data-label-on="Yes"
                        />
                      </label>
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Analytics Code */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Analytics Code</Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Analytics Code"
                        name="analytics_code"
                        type="textarea"
                        value={site.analytics_code || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* GTM container id — rendered as the GTM bootstrap on every
                    public page (SCRUM-744); GA4/UET live inside the container. */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">GTM Container ID</Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="GTM-XXXXXXX"
                        name="gtm"
                        type="text"
                        value={site.gtm || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Blog root — the Site's blogdir field, the root segment of the
                    blog breadcrumb (SCRUM-1235). Empty means the render-layer
                    default "blog"; the value is a segment, not a path. */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Blog root</Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="blog"
                        name="blogdir"
                        type="text"
                        title="Root segment of the blog breadcrumb; leave empty for the default blog"
                        value={site.blogdir || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Special Style Files */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Special Style Files</Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Special Style Files Code"
                        name="special_style_files"
                        type="textarea"
                        value={site.special_style_files || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Special Script Files */}
                <FormGroup className="row">
                  <Label md="2" className="form-control-label">Special Script Files</Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Special Script Files Code"
                        name="special_script_files"
                        type="textarea"
                        value={site.special_script_files || ""}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {canEdit && (
                  <Button color="primary" onClick={onClickUpdate} disabled={loading}>
                    {loading ? "Updating..." : "Update"}
                  </Button>
                )}
                <Button to={backLink} tag={Link} className="btn btn-default ml-2">
                  All Sites
                </Button>
              </CardBody>
            </Card>
          </Col>

          <Col className="order-xl-2" xl="4">
            <Card className="card-pricing border-0 text-center mb-4">
              <CardHeader className="bg-gradient-success">
                <h4 className="text-uppercase ls-0 text-white py-2 mb-0">Upload Logo</h4>
              </CardHeader>
              <CardBody className="px-lg-2">
                <ImageUploadContainer
                  ImageUpload={onImageUpload}
                  image={site.logo_pic || null}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
